import React, { useEffect, useState } from "react";
import { Star, StarHalf, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * META-2 · Universal Reviews UI.
 *
 * Drop this into any tab's entity-detail page and pass the two required
 * props:
 *   <ReviewSection entityType="cab_trip" entityId={delivery_id} />
 *
 * The component (a) fetches stats + list + AI summary, (b) shows a
 * "Write a review" form if the caller is signed-in and hasn't reviewed
 * yet — the backend enforces verified-transaction; we just show a
 * friendly error if it 403s.
 */
function Stars({ value, size = 12 }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  return (
    <span className="inline-flex items-center gap-[1px] text-amber-500" aria-label={`${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map(i => (
        i <= full
          ? <Star key={i} size={size} fill="currentColor" />
          : i === full + 1 && half
              ? <StarHalf key={i} size={size} fill="currentColor" />
              : <Star key={i} size={size} className="text-stone-200" fill="currentColor" />
      ))}
    </span>
  );
}

export default function ReviewSection({ entityType, entityId, entityLabel }) {
  const { user } = useAuth();
  const [data, setData]   = useState(null);
  const [loading, setL]   = useState(true);
  const [busy, setBusy]   = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [stars, setStars] = useState(5);
  const [body, setBody]   = useState("");
  const [photos, setPhotos] = useState([]);   // list of uploaded URLs
  const [uploadBusy, setUploadBusy] = useState(false);

  const load = async () => {
    setL(true);
    try {
      const r = await api.get(`/reviews/universal/${entityType}/${entityId}`);
      setData(r.data);
    } catch {
      setData({ count: 0, average: 0, distribution: {1:0,2:0,3:0,4:0,5:0}, items: [], summary: null });
    } finally { setL(false); }
  };
  useEffect(() => { if (entityType && entityId) load(); }, [entityType, entityId]); // eslint-disable-line

  const alreadyReviewed = !!(user && data?.items?.find(i => i.reviewer_id === user.user_id));

  const submit = async () => {
    if (!body.trim() && stars < 1) return;
    setBusy(true);
    try {
      await api.post("/reviews/universal", { entity_type: entityType, entity_id: entityId,
                                    rating: stars, body: body.trim(), photos });
      toast.success("Thanks — your review is live");
      setShowForm(false); setBody(""); setStars(5); setPhotos([]);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save your review";
      toast.error(typeof msg === "string" ? msg : "Review failed");
    } finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      // Reuse an existing media endpoint — falls back gracefully if absent.
      const r = await api.post("/media/upload", form, { headers: { "Content-Type": "multipart/form-data" }});
      const url = r.data?.url || r.data?.location;
      if (url) setPhotos(p => [...p, url].slice(0, 4));
      else     toast.error("Upload failed");
    } catch {
      toast.error("Upload failed — try again");
    } finally { setUploadBusy(false); }
  };

  if (loading) {
    return <div className="text-xs text-stone-400 py-6 text-center" data-testid="reviews-loading">Loading reviews…</div>;
  }

  return (
    <div className="mt-4 bg-white border border-stone-200 rounded-2xl p-4" data-testid={`reviews-section-${entityType}`}>
      <div className="flex items-baseline gap-3 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Reviews</div>
        {data.count > 0 ? (
          <>
            <Stars value={data.average} size={16} />
            <span className="text-lg font-black text-stone-900 tabular-nums">{data.average}</span>
            <span className="text-xs text-stone-500">({data.count} verified {data.count === 1 ? "review" : "reviews"})</span>
          </>
        ) : (
          <span className="text-xs text-stone-500">No reviews yet — be the first.</span>
        )}
        {user && !alreadyReviewed && (
          <button
            onClick={() => setShowForm(s => !s)}
            className="ml-auto text-[11px] font-bold rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5"
            data-testid="reviews-write-cta"
          >
            {showForm ? "Cancel" : "Write a review"}
          </button>
        )}
      </div>

      {/* Distribution bar */}
      {data.count > 0 && (
        <div className="mt-2 space-y-0.5 text-[10px] text-stone-500" data-testid="reviews-distribution">
          {[5,4,3,2,1].map(s => {
            const c = data.distribution?.[s] || 0;
            const pct = data.count ? Math.round(c / data.count * 100) : 0;
            return (
              <div key={s} className="flex items-center gap-1.5">
                <span className="w-3 tabular-nums">{s}★</span>
                <div className="flex-1 h-1 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full bg-amber-400" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right tabular-nums">{c}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* AI summary */}
      {data.summary && (
        <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-2.5 text-[12px] text-stone-800" data-testid="reviews-ai-summary">
          <div className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 mb-0.5">✨ AI summary</div>
          {data.summary}
        </div>
      )}

      {/* Write-a-review form */}
      {showForm && (
        <div className="mt-3 rounded-xl border border-stone-200 p-3 space-y-2" data-testid="reviews-form">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map(i => (
              <button key={i} type="button" onClick={() => setStars(i)}
                data-testid={`reviews-star-${i}`}>
                <Star size={22} className={i <= stars ? "text-amber-500" : "text-stone-200"} fill="currentColor" />
              </button>
            ))}
            <span className="ml-2 text-xs text-stone-500 tabular-nums">{stars}/5</span>
          </div>
          <textarea
            value={body} onChange={e => setBody(e.target.value)}
            placeholder={`Tell others how the ${entityLabel || entityType.replace("_", " ")} went…`}
            className="w-full border border-stone-200 rounded-lg p-2 text-sm min-h-[70px]"
            maxLength={2000}
            data-testid="reviews-body"
          />
          <div className="flex items-center gap-2 flex-wrap">
            {photos.map((u, i) => (
              <img key={i} src={u} alt="" className="w-12 h-12 rounded object-cover border border-stone-200"/>
            ))}
            {photos.length < 4 && (
              <label className="text-[11px] font-bold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 cursor-pointer">
                {uploadBusy ? <Loader2 size={11} className="animate-spin"/> : <Camera size={11}/>}
                Add photo
                <input type="file" accept="image/*" hidden onChange={e => uploadPhoto(e.target.files?.[0])} data-testid="reviews-photo-input"/>
              </label>
            )}
            <button
              onClick={submit}
              disabled={busy || stars < 1}
              className="ml-auto rounded-full bg-[#0F5132] hover:bg-[#0a3d24] text-white text-xs font-bold px-4 py-1.5 disabled:opacity-50"
              data-testid="reviews-submit"
            >
              {busy ? "Saving…" : "Post review"}
            </button>
          </div>
          <div className="text-[10px] text-stone-400">Only verified customers can post — we check server-side.</div>
        </div>
      )}

      {/* Item list */}
      {data.items?.length > 0 && (
        <div className="mt-3 divide-y divide-stone-100">
          {data.items.map(rv => (
            <div key={rv.review_id} className="py-2.5" data-testid={`reviews-item-${rv.review_id}`}>
              <div className="flex items-baseline gap-2">
                <Stars value={rv.rating} size={11} />
                <span className="text-[11px] font-bold text-stone-800">{rv.reviewer_name}</span>
                {rv.verified && <span className="text-[9px] text-emerald-700 bg-emerald-100 font-bold uppercase tracking-wide rounded-full px-1.5">Verified</span>}
                <span className="ml-auto text-[10px] text-stone-400">{new Date(rv.created_at).toLocaleDateString()}</span>
              </div>
              {rv.body && <div className="text-[13px] text-stone-800 mt-1 whitespace-pre-wrap">{rv.body}</div>}
              {rv.photos?.length > 0 && (
                <div className="mt-1.5 flex gap-1.5 flex-wrap">
                  {rv.photos.map((u, i) => (
                    <img key={i} src={u} alt="" className="w-14 h-14 rounded object-cover border border-stone-200"/>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
